import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { Link } from 'react-router-dom';
import { meService, TenantAccountPayload, TenantProfileEditable } from '../services/api';
import { resolveMediaUrl } from '../utils/mediaUrl';
import UserPhotoCell from '../components/UserPhotoCell';
import './TenantAccount.css';

const ACCEPT_IMAGE_PDF = 'image/*,application/pdf,.pdf';

function isEmpty(val?: string | null): boolean {
  return val == null || String(val).trim() === '';
}

function isPdfUrl(url: string): boolean {
  const p = url.split(/[?#]/)[0]?.toLowerCase() ?? '';
  return p.endsWith('.pdf');
}

function Detail({ label, value }: { label: string; value?: React.ReactNode }) {
  const empty = value == null || (typeof value === 'string' && isEmpty(value));
  return (
    <div className="ta-detail">
      <span className="ta-detail__label">{label}</span>
      <span className="ta-detail__value">{empty ? '—' : value}</span>
    </div>
  );
}

function IdDocBlock({ label, url }: { label: string; url?: string | null }) {
  if (isEmpty(url)) return null;
  const src = resolveMediaUrl(url) || url!;
  if (isPdfUrl(src)) {
    return (
      <div className="ta-id-doc">
        <span className="ta-id-doc__label">{label}</span>
        <a href={src} target="_blank" rel="noopener noreferrer" className="ta-link">
          View PDF
        </a>
      </div>
    );
  }
  return (
    <div className="ta-id-doc">
      <span className="ta-id-doc__label">{label}</span>
      <a href={src} target="_blank" rel="noopener noreferrer">
        <img src={src} alt={label} className="ta-id-doc__img" />
      </a>
    </div>
  );
}

const TenantAccount: React.FC = () => {
  const { refreshUser, isGuest } = useAuth();

  const [account, setAccount] = useState<TenantAccountPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [workingPlace, setWorkingPlace] = useState('');
  const [emergencyContactName, setEmergencyContactName] = useState('');
  const [emergencyContactPhoneNumber, setEmergencyContactPhoneNumber] = useState('');
  const [dob, setDob] = useState('');
  const [profilePhoto, setProfilePhoto] = useState<File | null>(null);
  const [govIdFront, setGovIdFront] = useState<File | null>(null);
  const [govIdBack, setGovIdBack] = useState<File | null>(null);

  const loadAccount = useCallback(async () => {
    try {
      setLoading(true);
      const data = await meService.getAccount();
      setAccount(data);
      const u = data.user;
      setEmail(u.email || '');
      setAddress(u.address || '');
      setWorkingPlace(u.workingPlace || '');
      setEmergencyContactName(u.emergencyContactName || '');
      setEmergencyContactPhoneNumber(u.emergencyContactPhoneNumber || '');
      setDob(u.dob ? String(u.dob).slice(0, 10) : '');
      setProfilePhoto(null);
      setGovIdFront(null);
      setGovIdBack(null);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadAccount();
  }, [loadAccount]);

  const user = account?.user;
  const editable = account?.editable;
  const location = account?.location;
  const guestStay = account?.guestStay;
  const guestMode = isGuest() || account?.accountKind === 'guest';

  const photoPreview = useMemo(
    () => (profilePhoto ? URL.createObjectURL(profilePhoto) : null),
    [profilePhoto],
  );

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editable?.canEditAny) {
      toast.info('All profile details are already saved.');
      return;
    }

    const fd = new FormData();
    let hasUpdates = false;
    if (editable.email && email.trim()) {
      fd.append('email', email.trim());
      hasUpdates = true;
    }
    if (editable.address && address.trim()) {
      fd.append('address', address.trim());
      hasUpdates = true;
    }
    if (editable.workingPlace && workingPlace.trim()) {
      fd.append('workingPlace', workingPlace.trim());
      hasUpdates = true;
    }
    if (editable.emergencyContactName && emergencyContactName.trim()) {
      fd.append('emergencyContactName', emergencyContactName.trim());
      hasUpdates = true;
    }
    if (editable.emergencyContactPhoneNumber && emergencyContactPhoneNumber.trim()) {
      fd.append('emergencyContactPhoneNumber', emergencyContactPhoneNumber.trim());
      hasUpdates = true;
    }
    if (editable.dob && dob) {
      fd.append('dob', dob);
      hasUpdates = true;
    }
    if (editable.photo && profilePhoto) {
      fd.append('photo', profilePhoto);
      hasUpdates = true;
    }
    if (editable.idFrontSide && govIdFront) {
      fd.append('idFrontSide', govIdFront);
      hasUpdates = true;
    }
    if (editable.idBackSide && govIdBack) {
      fd.append('idBackSide', govIdBack);
      hasUpdates = true;
    }

    if (!hasUpdates) {
      toast.error('Fill in at least one missing field to save.');
      return;
    }

    setSaving(true);
    try {
      const updated = await meService.updateProfile(fd);
      setAccount(updated);
      refreshUser(updated.user);
      toast.success('Profile saved. Saved fields cannot be changed again.');
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (
    key: keyof TenantProfileEditable,
    label: string,
    value: string | undefined,
    input: React.ReactNode,
  ) => {
    const canEdit = editable?.[key] === true;
    return (
      <div className={`ta-field${canEdit ? '' : ' ta-field--locked'}`}>
        <label>{label}</label>
        {canEdit ? (
          input
        ) : (
          <p className="ta-field__locked">
            {isEmpty(value) ? 'Not provided' : value}
            {!isEmpty(value) ? <span className="ta-field__badge">Locked</span> : null}
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="loading-container">Loading your profile…</div>;
  }

  if (!user) {
    return (
      <div className="ta-page">
        <p className="ta-empty">Unable to load profile. Try signing in again.</p>
      </div>
    );
  }

  return (
    <div className="ta-page">
      <header className="ta-header">
        <div>
          <h1>My profile</h1>
          <p className="ta-header__sub">View your details and complete any missing information once</p>
        </div>
      </header>

      <section className="ta-panel">
        <div className="ta-profile-hero">
          <UserPhotoCell name={user.username} photoUrl={user.photo} size="md" />
          <div>
            <h2>{user.username}</h2>
            <p>{user.phoneNumber}</p>
            {editable?.canEditAny ? (
              <p className="ta-hint">You can fill missing details once. After saving, they are locked.</p>
            ) : (
              <p className="ta-hint ta-hint--ok">
                Your profile is complete. Contact the hostel office to change details.
              </p>
            )}
          </div>
        </div>

          <div className="ta-card">
            <h3>{guestMode ? 'Current stay' : 'Stay details'}</h3>
            <div className="ta-grid">
              <Detail label="Floor" value={location?.floorNumber} />
              <Detail label="Room" value={location?.roomNumber} />
              <Detail label="Bed" value={location?.bedNumber} />
              {guestMode && guestStay ? (
                <>
                  <Detail label="Check-in" value={String(guestStay.checkInDate).slice(0, 10)} />
                  <Detail label="Check-out" value={String(guestStay.checkOutDate).slice(0, 10)} />
                  <Detail
                    label="Daily rate"
                    value={`₹${Number(guestStay.dailyRate || 0).toLocaleString('en-IN')}`}
                  />
                  <Detail
                    label="Balance due"
                    value={`₹${Number(guestStay.balanceDue || 0).toLocaleString('en-IN')}`}
                  />
                </>
              ) : (
                <>
                  <Detail
                    label="Rent"
                    value={user.rent != null ? `₹${Number(user.rent).toLocaleString('en-IN')}` : undefined}
                  />
                  <Detail
                    label="Advance paid"
                    value={user.advance != null ? `₹${Number(user.advance).toLocaleString('en-IN')}` : undefined}
                  />
                  <Detail
                    label="Joined"
                    value={user.dateOfJoining ? String(user.dateOfJoining).slice(0, 10) : undefined}
                  />
                  <Detail label="Payment day" value={user.paymentDate != null ? String(user.paymentDate) : undefined} />
                </>
              )}
            </div>
            {guestMode ? (
              <p className="ta-hint">
                <Link to="/my-stay">Pay balance or re-check-in</Link>
              </p>
            ) : null}
          </div>

        <form className="ta-card" onSubmit={(e) => void handleSaveProfile(e)}>
          <h3>Personal details</h3>
          <div className="ta-grid ta-grid--form">
            {renderField('email', 'Email', user.email, (
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
              />
            ))}
            {renderField('address', 'Address', user.address, (
              <textarea
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows={2}
                placeholder="Full address"
              />
            ))}
            {renderField('workingPlace', 'Work / college', user.workingPlace, (
              <input type="text" value={workingPlace} onChange={(e) => setWorkingPlace(e.target.value)} />
            ))}
            {renderField('dob', 'Date of birth', user.dob, (
              <input type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            ))}
            {renderField('emergencyContactName', 'Emergency contact name', user.emergencyContactName, (
              <input
                type="text"
                value={emergencyContactName}
                onChange={(e) => setEmergencyContactName(e.target.value)}
              />
            ))}
            {renderField(
              'emergencyContactPhoneNumber',
              'Emergency contact phone',
              user.emergencyContactPhoneNumber,
              (
                <input
                  type="tel"
                  value={emergencyContactPhoneNumber}
                  onChange={(e) => setEmergencyContactPhoneNumber(e.target.value)}
                />
              ),
            )}
          </div>

          <h3 className="ta-subheading">Photo &amp; ID proofs</h3>
          <div className="ta-upload-grid">
            <div className={`ta-upload${editable?.photo ? '' : ' ta-upload--locked'}`}>
              <span className="ta-upload__label">Profile photo</span>
              {user.photo && !editable?.photo ? (
                <img src={resolveMediaUrl(user.photo) || user.photo} alt="" className="ta-upload__preview" />
              ) : null}
              {editable?.photo ? (
                <>
                  {photoPreview ? <img src={photoPreview} alt="" className="ta-upload__preview" /> : null}
                  <input
                    type="file"
                    accept={ACCEPT_IMAGE_PDF}
                    onChange={(e) => setProfilePhoto(e.target.files?.[0] || null)}
                  />
                </>
              ) : !user.photo ? (
                <span className="ta-field__locked">Not provided</span>
              ) : null}
            </div>
            <div className={`ta-upload${editable?.idFrontSide ? '' : ' ta-upload--locked'}`}>
              <span className="ta-upload__label">ID front</span>
              <IdDocBlock label="ID front" url={user.idFrontSide} />
              {editable?.idFrontSide ? (
                <input
                  type="file"
                  accept={ACCEPT_IMAGE_PDF}
                  onChange={(e) => setGovIdFront(e.target.files?.[0] || null)}
                />
              ) : null}
            </div>
            <div className={`ta-upload${editable?.idBackSide ? '' : ' ta-upload--locked'}`}>
              <span className="ta-upload__label">ID back</span>
              <IdDocBlock label="ID back" url={user.idBackSide} />
              {editable?.idBackSide ? (
                <input
                  type="file"
                  accept={ACCEPT_IMAGE_PDF}
                  onChange={(e) => setGovIdBack(e.target.files?.[0] || null)}
                />
              ) : null}
            </div>
          </div>

          {editable?.canEditAny ? (
            <div className="ta-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving…' : 'Save missing details'}
              </button>
            </div>
          ) : null}
        </form>
      </section>
    </div>
  );
};

export default TenantAccount;
